import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { ICertificateTypeListItemFragment } from 'graphql/types';
import { PageDrawer } from 'common/components/PageDrawer';
import { FilterBar } from 'common/components/FilterBar';
import { Loader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { CertificateSelectorListItem } from 'common/components/CertificateSelectorListItem';
import { useCertificateList } from 'certificate/hooks';

interface IProps {
    edit?: boolean;
    open?: boolean;
    inDrawer?: boolean;
    onClose?(): void;
    onSelect?(certificate: ICertificateTypeListItemFragment): void;
}

export const CertificateModuleSelectDrawer = ({
    edit,
    inDrawer = true,
    open,
    onClose,
    onSelect,
}: IProps) => {
    const [translate] = useTranslation();

    const {
        certificates,
        loading,
        paginationSettings,
        filters,
        filterValues,
        setPage,
        onSearch,
        onFilterChange,
    } = useCertificateList(false, { external: false });

    const titleSelect = translate(
        `moduleForm.certificate.${
            edit ? 'editTitleCertificateSelect' : 'newTitleCertificateSelect'
        }`
    );
    const textSelect = translate(
        `moduleForm.certificate.${
            edit ? 'editTextCertificateSelect' : 'newTextCertificateSelect'
        }`
    );

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const drawerContent = open ? (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontWeight: 'bold' }} variant="h3">
                    {titleSelect}
                </Typography>

                <Box sx={{ mt: 1 }}>
                    <Typography>{textSelect}</Typography>
                </Box>
            </Box>

            <FilterBar
                useFilterDrawer
                disabled={loading}
                filterDrawerProps={{
                    title: translate('filterForm.certificates.title'),
                    description: translate(
                        'filterForm.certificates.description'
                    ),
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);

                    onFilterChange?.(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {loading && <Loader />}

            {!loading && !certificates.length && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 7,
                    }}
                >
                    <Typography>
                        {translate('noOptionsText.certificates')}
                    </Typography>
                </Box>
            )}

            {!loading && !!certificates.length && (
                <>
                    {certificates.map((certificate) => (
                        <CertificateSelectorListItem
                            certificate={certificate}
                            key={certificate.id}
                            px={0}
                            onClick={() => onSelect?.(certificate)}
                        />
                    ))}

                    {paginationSettings.count > -1 && (
                        <Box sx={{ mt: 4 }}>
                            <Pagination
                                page={paginationSettings.page}
                                pageAmount={paginationSettings.pageAmount}
                                totalsAmount={paginationSettings.count}
                                totalsText={translate('certificate', {
                                    count:
                                        paginationSettings.count >= 0
                                            ? paginationSettings.count
                                            : 0,
                                })}
                                onChange={(page) => setPage(page)}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    ) : (
        <Loader />
    );

    if (!inDrawer) return drawerContent;

    return (
        <PageDrawer open={open} onClose={onClose}>
            {drawerContent}
        </PageDrawer>
    );
};
